"use client";

import { useEffect, useState, useCallback } from "react";
import Link from "next/link";
import { useSearchParams, useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { StarRating } from "@/components/shared/star-rating";
import { FundCard } from "@/components/funds/fund-card";
import { FundCategoryChips } from "@/components/funds/fund-category-chips";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency } from "@/lib/utils/format-currency";
import { SECTORS } from "@/lib/utils/constants";

type BrowseType = "stocks" | "funds";

type BrowseStockReport = {
  id: string;
  slug: string | null;
  ticker: string;
  exchange: string;
  companyName: string;
  sector: string;
  currentPrice: number;
  currency: string;
  score: number;
  fairValue: { low: number; high: number; methodology: string } | null;
  thesis: string | null;
  createdAt: string;
};

type BrowseFundReport = {
  id: string;
  slug: string;
  wtmScore: number | null;
  summaryText: string | null;
  fund: {
    schemeCode: string;
    schemeName: string;
    amc: string | null;
    category: string | null;
    fundType: string | null;
    nav: number | null;
    aum: number | null;
    expenseRatio: number | null;
    morningstarRating: number | null;
    currency: string;
  };
};

type MostRequested = {
  stockName: string;
  requestCount: number;
};

const STOCK_SORTS = [
  { value: "newest", label: "Newest" },
  { value: "score", label: "Highest Score" },
];

const FUND_SORTS = [
  { value: "newest", label: "Newest" },
  { value: "top-rated", label: "Top Rated" },
  { value: "most-viewed", label: "Most Viewed" },
];

export function BrowseClient() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { status: authStatus } = useSession();

  const rawType = searchParams.get("type");
  const activeType: BrowseType = rawType === "funds" ? "funds" : "stocks";
  const activeSector = searchParams.get("sector") ?? "";
  const activeCategory = searchParams.get("category");
  const activeSort =
    searchParams.get("sort") ?? (activeType === "funds" ? "newest" : "newest");

  const [stockReports, setStockReports] = useState<BrowseStockReport[]>([]);
  const [fundReports, setFundReports] = useState<BrowseFundReport[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  // Request analysis — stocks only
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestName, setRequestName] = useState("");
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [mostRequested, setMostRequested] = useState<MostRequested[]>([]);

  const fetchStocks = useCallback(async (sector: string, sort: string, pg: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sector) params.set("sector", sector);
      params.set("sort", sort);
      params.set("page", String(pg));

      const res = await fetch(`/api/reports/browse?${params}`);
      const json = await res.json();
      if (json.success) {
        setStockReports(json.data.reports);
        setTotal(json.data.total);
        setPage(json.data.page);
        setTotalPages(json.data.totalPages);
      }
    } catch {
      // Browse fetch failed
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFunds = useCallback(async (category: string | null, sort: string, pg: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      params.set("sort", sort);
      params.set("page", String(pg));

      const res = await fetch(`/api/funds/browse?${params}`);
      const json = await res.json();
      if (json.success) {
        setFundReports(json.data.reports);
        setTotal(json.data.pagination.totalCount);
        setPage(json.data.pagination.page);
        setTotalPages(json.data.pagination.totalPages);
      }
    } catch {
      // Browse fetch failed
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMostRequested = useCallback(async () => {
    try {
      const res = await fetch("/api/stocks/request");
      const json = await res.json();
      if (json.success) setMostRequested(json.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (activeType === "funds") {
      fetchFunds(activeCategory, activeSort, 1);
    } else {
      fetchStocks(activeSector, activeSort, 1);
      fetchMostRequested();
    }
  }, [
    activeType,
    activeSector,
    activeCategory,
    activeSort,
    fetchStocks,
    fetchFunds,
    fetchMostRequested,
  ]);

  function updateFilter(key: string, value: string) {
    const params = new URLSearchParams(searchParams.toString());
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete("page");
    router.push(`/browse?${params.toString()}`);
  }

  function switchType(next: BrowseType) {
    const params = new URLSearchParams();
    if (next === "funds") params.set("type", "funds");
    // sort/sector/category reset on type switch — they don't translate across
    router.push(`/browse?${params.toString()}`);
  }

  function goToPage(pg: number) {
    if (activeType === "funds") {
      fetchFunds(activeCategory, activeSort, pg);
    } else {
      fetchStocks(activeSector, activeSort, pg);
    }
  }

  async function submitRequest() {
    if (!requestName.trim()) return;
    setRequestSubmitting(true);
    try {
      const res = await fetch("/api/stocks/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stockName: requestName.trim() }),
      });
      const json = await res.json();
      if (json.success) {
        setRequestSuccess(true);
        setRequestName("");
        fetchMostRequested();
        setTimeout(() => {
          setShowRequestModal(false);
          setRequestSuccess(false);
        }, 1500);
      }
    } catch { /* ignore */ }
    setRequestSubmitting(false);
  }

  const sortOptions = activeType === "funds" ? FUND_SORTS : STOCK_SORTS;
  const newReportHref = activeType === "funds" ? "/funds" : "/search";
  const newReportLabel =
    activeType === "funds" ? "+ New Fund Report" : "+ New Report";
  const headerSubtitle =
    activeType === "funds"
      ? `${total} public fund report${total !== 1 ? "s" : ""} generated`
      : `${total} public stock report${total !== 1 ? "s" : ""} generated by the community`;

  return (
    <>
      {/* Count + actions */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <p className="text-muted-foreground text-sm">{headerSubtitle}</p>
        <div className="flex gap-2">
          {activeType === "stocks" && (
            <button
              onClick={() => {
                if (authStatus !== "authenticated") {
                  router.push("/login?callbackUrl=/browse");
                  return;
                }
                setShowRequestModal(true);
              }}
              className="border border-surface-light/30 text-muted-foreground font-semibold text-sm px-4 py-2.5 rounded-lg hover:border-blue/40 hover:text-foreground transition cursor-pointer bg-transparent"
            >
              Request Analysis
            </button>
          )}
          <Link
            href={newReportHref}
            className="bg-blue text-white font-semibold text-sm px-5 py-2.5 rounded-lg hover:opacity-90 transition no-underline"
          >
            {newReportLabel}
          </Link>
        </div>
      </div>

      {/* Type tabs */}
      <div className="flex gap-2 mb-5">
        {(["stocks", "funds"] as const).map((t) => (
          <button
            key={t}
            onClick={() => switchType(t)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition cursor-pointer border ${
              activeType === t
                ? "bg-blue/15 text-blue border-blue/40"
                : "bg-surface text-muted-foreground border-surface-light/30 hover:border-blue/30 hover:text-foreground"
            }`}
          >
            {t === "stocks" ? "Stocks" : "Mutual Funds"}
          </button>
        ))}
      </div>

      {/* Most Requested — stocks only */}
      {activeType === "stocks" && mostRequested.length > 0 && (
        <div className="mb-6">
          <div className="text-dim text-[10px] uppercase tracking-widest mb-2">Most Requested</div>
          <div className="flex flex-wrap gap-2">
            {mostRequested.map((r) => (
              <span
                key={r.stockName}
                className="bg-surface border border-surface-light/30 rounded-full px-3 py-1.5 text-[12px] text-muted-foreground"
              >
                {r.stockName}
                <span className="text-blue ml-1.5 font-semibold font-tabular">{r.requestCount}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      {activeType === "stocks" ? (
        <div className="flex flex-wrap gap-3 mb-6">
          <select
            value={activeSector}
            onChange={(e) => updateFilter("sector", e.target.value)}
            className="bg-surface border border-surface-light/30 rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:border-blue/50 transition appearance-none cursor-pointer"
          >
            <option value="">All Sectors</option>
            {SECTORS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <select
            value={activeSort}
            onChange={(e) => updateFilter("sort", e.target.value)}
            className="bg-surface border border-surface-light/30 rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:border-blue/50 transition appearance-none cursor-pointer"
          >
            {STOCK_SORTS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <FundCategoryChips
              activeCategory={activeCategory}
              onSelect={(cat) => updateFilter("category", cat ?? "")}
            />
          </div>
          <div className="flex items-center gap-4 mb-5 border-b border-surface-light/20 pb-2">
            {sortOptions.map((s) => (
              <button
                key={s.value}
                onClick={() => updateFilter("sort", s.value)}
                className={`text-sm pb-1 border-b-2 transition cursor-pointer bg-transparent border-x-0 border-t-0 ${
                  activeSort === s.value
                    ? "text-blue border-blue font-semibold"
                    : "text-muted-foreground border-transparent hover:text-foreground"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Loading */}
      {loading && activeType === "stocks" && (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-blue/30 border-t-blue rounded-full animate-spin" />
        </div>
      )}
      {loading && activeType === "funds" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-xl" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && activeType === "stocks" && stockReports.length === 0 && (
        <div className="bg-surface border border-surface-light/30 rounded-xl p-10 text-center">
          <p className="text-muted-foreground text-sm mb-4">
            No reports found{activeSector ? ` in ${activeSector}` : ""}.
          </p>
          <Link
            href="/search"
            className="bg-blue text-white font-semibold text-sm px-5 py-2.5 rounded-lg hover:opacity-90 transition no-underline inline-block"
          >
            Generate the First One
          </Link>
        </div>
      )}
      {!loading && activeType === "funds" && fundReports.length === 0 && (
        <div className="bg-surface border border-surface-light/30 rounded-xl p-10 text-center">
          <p className="text-muted-foreground text-sm mb-4">
            No fund reports found{activeCategory ? ` for "${activeCategory}"` : ""}.
          </p>
          <Link
            href="/funds"
            className="bg-blue text-white font-semibold text-sm px-5 py-2.5 rounded-lg hover:opacity-90 transition no-underline inline-block"
          >
            Find a Fund to Analyze
          </Link>
        </div>
      )}

      {/* Stock cards */}
      {!loading && activeType === "stocks" && stockReports.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {stockReports.map((r) => (
            <Link
              key={r.id}
              href={`/report/${r.slug ?? r.id}`}
              className="bg-surface border border-surface-light/30 rounded-xl p-5 hover:border-blue/40 transition no-underline group"
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="text-foreground font-semibold text-[15px] group-hover:text-blue transition">
                    {r.companyName}
                  </span>
                  <span className="text-dim text-[11px] ml-2">
                    {r.exchange}:{r.ticker}
                  </span>
                </div>
                <StarRating score={r.score} size="text-sm" />
              </div>

              <div className="flex items-center gap-3 text-dim text-[11px] mb-3">
                <span>{r.sector}</span>
                <span>&bull;</span>
                <span className="font-tabular">{formatCurrency(r.currentPrice, r.currency as "INR" | "USD" | "AUD")}</span>
                <span>&bull;</span>
                <span>{new Date(r.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
              </div>

              {r.fairValue && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-dim text-[11px]">Fair Value:</span>
                  <span className="text-cgreen text-[12px] font-semibold font-tabular">
                    {formatCurrency(r.fairValue.low, r.currency as "INR" | "USD" | "AUD")}
                    {" - "}
                    {formatCurrency(r.fairValue.high, r.currency as "INR" | "USD" | "AUD")}
                  </span>
                  <span className="text-dim text-[10px]">({r.fairValue.methodology})</span>
                </div>
              )}

              {r.thesis && (
                <p className="text-muted-foreground text-[12px] leading-relaxed line-clamp-2">
                  {r.thesis}
                </p>
              )}
            </Link>
          ))}
        </div>
      )}

      {/* Fund cards */}
      {!loading && activeType === "funds" && fundReports.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fundReports.map((r) => (
            <FundCard
              key={r.id}
              slug={r.slug}
              schemeName={r.fund.schemeName}
              amc={r.fund.amc ?? ""}
              category={r.fund.category ?? ""}
              wtmScore={r.wtmScore ?? 0}
              expenseRatio={r.fund.expenseRatio ?? 0}
              returns1Y={null}
              returns3Y={null}
              aum={r.fund.aum}
              currency={r.fund.currency}
              summaryText={r.summaryText ?? undefined}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            onClick={() => goToPage(page - 1)}
            disabled={page <= 1}
            className="bg-surface border border-surface-light/30 rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-default transition"
          >
            Previous
          </button>
          <span className="text-dim text-sm px-3 font-tabular">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => goToPage(page + 1)}
            disabled={page >= totalPages}
            className="bg-surface border border-surface-light/30 rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-default transition"
          >
            Next
          </button>
        </div>
      )}

      {/* Request Analysis Modal — stocks only */}
      {showRequestModal && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[200] backdrop-blur-sm"
          onClick={() => { setShowRequestModal(false); setRequestSuccess(false); }}
        >
          <div
            className="bg-navy border border-blue/25 rounded-xl p-6 max-w-[440px] w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-foreground text-2xl font-serif mb-2">Request a Stock Analysis</h2>
            <p className="text-muted-foreground text-sm mb-5">
              Can&apos;t find a stock? Tell us what you&apos;d like analyzed and we&apos;ll prioritize it.
            </p>

            {requestSuccess ? (
              <div className="text-center py-4">
                <span className="text-cgreen text-3xl">✓</span>
                <p className="text-foreground font-semibold mt-2">Request submitted!</p>
              </div>
            ) : (
              <>
                <input
                  value={requestName}
                  onChange={(e) => setRequestName(e.target.value)}
                  placeholder="e.g., Bajaj Finance, Nvidia, Tata Motors..."
                  className="w-full bg-surface border border-surface-light/30 rounded-lg px-4 py-3 text-foreground text-sm outline-none focus:border-blue/50 transition placeholder:text-dim mb-4"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter") submitRequest(); }}
                />
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowRequestModal(false)}
                    className="flex-1 border border-surface-light/30 text-muted-foreground py-2.5 rounded-lg text-sm hover:text-foreground transition cursor-pointer bg-transparent"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitRequest}
                    disabled={!requestName.trim() || requestSubmitting}
                    className="flex-1 bg-blue text-white py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition cursor-pointer disabled:opacity-40 disabled:cursor-default border-none"
                  >
                    {requestSubmitting ? "Submitting..." : "Submit Request"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
